import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ShoppingBag, Banknote, FileText, AlertTriangle, ExternalLink, Copy, Package, MoreHorizontal, Pencil, Eye, EyeOff, Trash2, Loader2, CheckCircle2, Wallet, ReceiptText, Search, ArrowUpDown, ArrowUp, ArrowDown, QrCode, X, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { ShopNav } from "../components/shop/ShopNav";
import { ProductImage } from "../components/shop/ProductCard";
import { LabelPrintDialog } from "../components/shop/LabelPrintDialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { nok, orderStatusMeta, productUrl } from "../lib/shop";
import { useLang } from "../context/LanguageContext";

function productStatusMeta(p, t) {
  if (!p.active) return { label: t("Skjult"), className: "bg-secondary text-secondary-foreground border border-border hover:bg-secondary" };
  if (p.stock_status === "sold_out") return { label: t("Utsolgt"), className: "bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]" };
  if (p.stock_status === "low") return { label: t("Lav beholdning"), className: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]" };
  return { label: t("Aktiv"), className: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" };
}

// Lagerfelt med inline-redigering (Enter = lagre, Esc = angre) – design: product_table.inline_stock_edit
function StockCell({ product, onSaved, sfx = "" }) {
  const { t } = useLang();
  const [value, setValue] = useState(String(product.stock));
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(String(product.stock)), [product.stock]);
  const dirty = value !== "" && Number(value) !== product.stock;
  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const res = await api.patch(`/my/shop/products/${product.id}/stock`, { stock: Math.max(0, parseInt(value, 10) || 0) });
      onSaved(res.data.product);
      toast.success(t("Lager oppdatert"));
    } catch (err) {
      toast.error(errorMessage(err));
      setValue(String(product.stock));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setValue(String(product.stock));
        }}
        className="h-9 w-20 tabular-nums"
        aria-label={t("Lager")}
        title={t("Enter = lagre, Esc = angre")}
        data-testid={`product-table-stock-input-${product.id}${sfx}`}
      />
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" /> : dirty ? <span className="w-2 h-2 rounded-full bg-[hsl(var(--info))]" title={t("Endret lager")} aria-label={t("Endret lager")} /> : null}
    </div>
  );
}

// Sortering av varelista (URL-param «sort» = key:dir). «created» = nyaste først (standard, slik serveren leverer)
const SORT_KEYS = ["created", "name", "sku", "price", "stock", "sold", "updated"];
const SORT_LABELS = { created: "Nyeste først", name: "Navn", sku: "Varenummer", price: "Pris", stock: "Lager", sold: "Solgt", updated: "Sist oppdatert" };
const collator = new Intl.Collator("nb", { numeric: true, sensitivity: "base" });

function sortProducts(list, key, dir) {
  const sign = dir === "desc" ? -1 : 1;
  const cmp = {
    created: (a, b) => collator.compare(a.created_at || "", b.created_at || ""),
    name: (a, b) => collator.compare(a.name || "", b.name || ""),
    sku: (a, b) => (!a.sku && b.sku ? 1 : a.sku && !b.sku ? -1 : collator.compare(a.sku || "", b.sku || "")),
    price: (a, b) => (a.price || 0) - (b.price || 0),
    stock: (a, b) => (a.stock || 0) - (b.stock || 0),
    sold: (a, b) => (a.sold_count || 0) - (b.sold_count || 0),
    updated: (a, b) => collator.compare(a.updated_at || "", b.updated_at || ""),
  }[key] || (() => 0);
  return [...list].sort((a, b) => sign * cmp(a, b) || collator.compare(a.name || "", b.name || ""));
}

export default function ShopDashboardPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [printTargets, setPrintTargets] = useState(null); // liste med varer til etikett-dialogen

  const [sortKey, sortDir] = useMemo(() => {
    const [k, d] = (params.get("sort") || "created:desc").split(":");
    return [SORT_KEYS.includes(k) ? k : "created", d === "asc" ? "asc" : "desc"];
  }, [params]);

  const setSort = (key, dir) => {
    const next = new URLSearchParams(params);
    if (key === "created" && dir === "desc") next.delete("sort"); else next.set("sort", `${key}:${dir}`);
    setParams(next, { replace: true });
  };

  // Klikk på kolonneoverskrift: første klikk = stigande (tal: synkande), andre klikk = motsett, tredje = tilbake til nyaste først
  const toggleSort = (key) => {
    const numeric = ["price", "stock", "sold"].includes(key);
    if (sortKey !== key) return setSort(key, numeric ? "desc" : "asc");
    if ((numeric && sortDir === "desc") || (!numeric && sortDir === "asc")) return setSort(key, numeric ? "asc" : "desc");
    return setSort("created", "desc");
  };

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q.trim()) next.set("q", q.trim()); else next.delete("q");
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/my/shop");
      setData(res.data);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const replaceProduct = (p) => setData((d) => ({ ...d, products: d.products.map((x) => (x.id === p.id ? p : x)) }));

  const toggleEnabled = async (enabled) => {
    setToggling(true);
    try {
      const res = await api.put("/my/shop/settings", { ...data.settings, enabled });
      setData((d) => ({ ...d, settings: res.data.settings, can_sell: res.data.can_sell, vipps_available: res.data.vipps_available, invoice_available: res.data.invoice_available }));
      toast.success(enabled ? t("Butikken er åpnet") : t("Butikken er stengt for publikum"));
      if (res.data.warning) toast.warning(res.data.warning);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setToggling(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      const res = await api.patch(`/my/shop/products/${p.id}/active`);
      replaceProduct(res.data.product);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const markSoldOut = async (p) => {
    try {
      const res = await api.patch(`/my/shop/products/${p.id}/stock`, { stock: 0 });
      replaceProduct(res.data.product);
      toast.success(t("Lager oppdatert"));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const remove = async () => {
    const p = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/my/shop/products/${p.id}`);
      setData((d) => ({ ...d, products: d.products.filter((x) => x.id !== p.id) }));
      toast.success(t("Varen er slettet"));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.public_url);
      toast.success(t("Lenken er kopiert"));
    } catch {
      toast.error(t("Noe gikk galt"));
    }
  };

  const m = data?.metrics;
  const products = data?.products || [];
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = needle ? products.filter((p) => `${p.name} ${p.sku || ""}`.toLowerCase().includes(needle)) : products;
    return sortProducts(filtered, sortKey, sortDir);
  }, [products, q, sortKey, sortDir]);

  const SortHead = ({ k, children, className = "" }) => {
    const active = sortKey === k;
    const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
    return (
      <TableHead className={className} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
        <button type="button" onClick={() => toggleSort(k)} className={`inline-flex items-center gap-1 rounded-md -mx-1 px-1 py-0.5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? "text-foreground font-semibold" : ""}`} data-testid={`product-sort-${k}`}>
          {children} <Icon className={`w-3.5 h-3.5 ${active ? "" : "opacity-50"}`} />
        </button>
      </TableHead>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="shop-dashboard">
        <ShopNav openCount={m?.to_fulfill || 0} />

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-28 w-full rounded-xl" />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        ) : error || !data ? (
          <Card className="border-border"><CardContent className="p-8 text-center space-y-3">
            <p className="text-muted-foreground">{t("Kunne ikke laste butikken")}</p>
            <Button onClick={() => { setLoading(true); load(); }} data-testid="shop-retry-button">{t("Prøv igjen")}</Button>
          </CardContent></Card>
        ) : (
          <>
            {/* Status + hurtighandlinger */}
            <Card className="border-border" data-testid="shop-status-card">
              <CardContent className="p-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-1.5 min-w-0">
                  <p className="text-sm text-muted-foreground">{t("Selg bøker, suvenirer og andre varer ved siden av billettene – med lager, Vipps og faktura.")}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer" data-testid="shop-enabled-toggle-wrap">
                      <Switch checked={!!data.settings.enabled} onCheckedChange={toggleEnabled} disabled={toggling} data-testid="shop-enabled-switch" />
                      <span className="text-sm font-medium" data-testid="shop-enabled-label">{data.settings.enabled ? t("Butikken er åpen") : t("Butikken er stengt")}</span>
                    </label>
                    <Badge variant="outline" className={`gap-1 ${data.vipps_available ? "text-[hsl(var(--success))] border-[hsl(var(--success))]/40" : "text-muted-foreground"}`} data-testid="shop-vipps-badge">
                      <Wallet className="w-3 h-3" /> {data.vipps_available ? t("Vipps er klart") : t("Vipps mangler")}
                    </Badge>
                    <Badge variant="outline" className={`gap-1 ${data.invoice_available ? "text-[hsl(var(--success))] border-[hsl(var(--success))]/40" : "text-muted-foreground"}`} data-testid="shop-invoice-badge">
                      <ReceiptText className="w-3 h-3" /> {data.invoice_available ? t("Faktura er klart") : t("Faktura mangler kontonummer")}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button asChild data-testid="shop-create-product-button"><Link to="/dashboard/butikk/ny"><Plus className="w-4 h-4 mr-1.5" /> {t("Opprett vare")}</Link></Button>
                  <Button asChild variant="outline" data-testid="shop-open-public-button"><a href={data.public_url} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1.5" /> {t("Åpne offentlig butikk")}</a></Button>
                  <Button variant="ghost" onClick={copyLink} data-testid="shop-copy-link-button"><Copy className="w-4 h-4 mr-1.5" /> {t("Kopier lenke")}</Button>
                </div>
              </CardContent>
            </Card>

            {data.settings.enabled && !data.can_sell && (
              <div className="rounded-xl border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10 p-4 flex gap-3 text-sm" data-testid="shop-no-payment-warning">
                <AlertTriangle className="w-5 h-5 text-[hsl(var(--warning))] shrink-0" />
                <div>
                  <p className="font-medium">{t("Ingen betalingsmåte er klar")}</p>
                  <p className="text-muted-foreground">{t("Legg inn kontonummer for faktura under Innstillinger, eller sett opp Vipps under Betaling. Kunder kan se varene, men ikke bestille før én av dem er på plass.")}</p>
                </div>
              </div>
            )}

            {/* Metrics */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="shop-metrics">
              {[
                { title: t("Salg"), value: m.orders, sub: t("{n} betalte", { n: m.paid_orders }), Icon: ShoppingBag, testId: "butikk-metric-sales" },
                { title: t("Omsetning"), value: nok(m.revenue), sub: t("Right in tar ingen avgift på butikksalg."), Icon: Banknote, testId: "butikk-metric-revenue" },
                { title: t("Åpne fakturaer"), value: m.open_invoices, sub: `${nok(m.open_invoices_sum)}${m.overdue_invoices ? ` · ${t("{n} forfalt", { n: m.overdue_invoices })}` : ""}`, Icon: FileText, testId: "butikk-metric-open-invoices", warn: m.overdue_invoices > 0 },
                { title: t("Lav lagerbeholdning"), value: m.low_stock, sub: t("{n} utsolgt", { n: m.sold_out }), Icon: AlertTriangle, testId: "butikk-metric-low-stock", warn: m.low_stock > 0 },
              ].map(({ title, value, sub, Icon, testId, warn }) => (
                <Card key={testId} className="border-border">
                  <CardContent className="p-5 flex items-start gap-3">
                    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0 ${warn ? "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]" : "bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand))]"}`}><Icon className="w-5 h-5" /></span>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">{title}</p>
                      <p className="text-2xl sm:text-3xl font-heading font-semibold tabular-nums" data-testid={testId}>{value}</p>
                      <p className="text-xs text-muted-foreground truncate">{sub}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Varer */}
            <section className="space-y-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-lg font-heading font-semibold tracking-tight flex items-center gap-2"><Package className="w-5 h-5 text-muted-foreground" /> {t("Varer")} <span className="text-sm font-normal text-muted-foreground">({q.trim() ? `${visible.length} / ${products.length}` : products.length})</span></h2>
                {products.length > 0 && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="relative sm:w-64">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Søk på navn eller varenummer")} className="pl-9 pr-8" aria-label={t("Søk på navn eller varenummer")} data-testid="product-search-input" />
                      {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={t("Tøm søk")} data-testid="product-search-clear"><X className="w-4 h-4" /></button>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <div className="md:hidden flex-1 min-w-[9rem]">
                        <Select value={`${sortKey}:${sortDir}`} onValueChange={(v) => { const [k, d] = v.split(":"); setSort(k, d); }}>
                          <SelectTrigger aria-label={t("Sorter")} data-testid="product-sort-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="created:desc">{t("Nyeste først")}</SelectItem>
                            <SelectItem value="name:asc">{t("Navn")} A–Å</SelectItem>
                            <SelectItem value="sku:asc">{t("Varenummer")} ↑</SelectItem>
                            <SelectItem value="price:desc">{t("Pris")} ↓</SelectItem>
                            <SelectItem value="price:asc">{t("Pris")} ↑</SelectItem>
                            <SelectItem value="stock:asc">{t("Lager")} ↑</SelectItem>
                            <SelectItem value="sold:desc">{t("Solgt")} ↓</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button variant="outline" onClick={() => setPrintTargets(visible)} disabled={visible.length === 0} title={t("Hyllekort eller etikettark med strekkode og QR")} data-testid="product-print-labels-button">
                        <QrCode className="w-4 h-4 mr-1.5" /> {t("Strekkoder")}{q.trim() && visible.length > 0 ? ` (${visible.length})` : ""}
                      </Button>
                      <Button asChild variant="outline" title={t("Telleliste til utskrift og registrering av opptalt antall")} data-testid="product-stock-count-button">
                        <Link to="/dashboard/butikk/lagerteljing"><ClipboardList className="w-4 h-4 mr-1.5" /> {t("Lagertelling")}</Link>
                      </Button>
                      {m.to_fulfill > 0 && (
                        <Button asChild variant="outline" className="whitespace-normal" data-testid="shop-open-orders-button"><Link to="/dashboard/butikk/ordrer?status=open">{t("Til behandling")}: {m.to_fulfill}</Link></Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {products.length === 0 ? (
                <Card className="border-dashed border-border" data-testid="shop-empty-products">
                  <CardContent className="p-10 text-center space-y-3">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]"><Package className="w-6 h-6" /></span>
                    <p className="font-heading font-semibold">{t("Ingen varer ennå")}</p>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">{t("Legg til din første vare for å begynne å selge sammen med billettene.")}</p>
                    <Button asChild data-testid="empty-products-create-button"><Link to="/dashboard/butikk/ny"><Plus className="w-4 h-4 mr-1.5" /> {t("Opprett vare")}</Link></Button>
                  </CardContent>
                </Card>
              ) : visible.length === 0 ? (
                <Card className="border-dashed border-border" data-testid="shop-no-search-hits">
                  <CardContent className="p-8 text-center space-y-2">
                    <p className="font-heading font-semibold">{t("Ingen varer passet søket")}</p>
                    <p className="text-sm text-muted-foreground">{t("Prøv et annet navn eller varenummer.")}</p>
                    <Button variant="ghost" size="sm" onClick={() => setQ("")} data-testid="product-search-reset">{t("Tøm søk")}</Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Desktop: tabell */}
                  <Card className="border-border hidden md:block">
                    <Table data-testid="product-table">
                      <TableHeader>
                        <TableRow>
                          <SortHead k="name">{t("Vare")}</SortHead>
                          <SortHead k="sku">{t("Varenr.")}</SortHead>
                          <SortHead k="price" className="text-right">{t("Pris")}</SortHead>
                          <SortHead k="stock">{t("Lager")}</SortHead>
                          <SortHead k="sold" className="text-right">{t("Solgt")}</SortHead>
                          <TableHead>{t("Status")}</TableHead>
                          <SortHead k="updated">{t("Sist oppdatert")}</SortHead>
                          <TableHead className="text-right">{t("Handlinger")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visible.map((p) => {
                          const st = productStatusMeta(p, t);
                          return (
                            <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                              <TableCell>
                                <Link to={`/dashboard/butikk/vare/${p.id}`} className="flex items-center gap-3 min-w-0 group" data-testid={`product-row-link-${p.id}`}>
                                  <span className="w-12 h-9 rounded-md overflow-hidden bg-muted shrink-0"><ProductImage product={p} /></span>
                                  <span className="block font-medium truncate group-hover:underline underline-offset-2">{p.name}</span>
                                </Link>
                              </TableCell>
                              <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap" data-testid={`product-sku-${p.id}`}>{p.sku || "–"}</TableCell>
                              <TableCell className="text-right tabular-nums">{nok(p.price)}</TableCell>
                              <TableCell><StockCell product={p} onSaved={replaceProduct} /></TableCell>
                              <TableCell className="text-right tabular-nums">{p.sold_count || 0}</TableCell>
                              <TableCell><Badge className={st.className} data-testid={`product-status-${p.id}`}>{st.label}</Badge></TableCell>
                              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(p.updated_at)}</TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" aria-label={t("Handlinger")} data-testid={`product-table-row-actions-${p.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => navigate(`/dashboard/butikk/vare/${p.id}`)} data-testid={`product-action-edit-${p.id}`}><Pencil className="w-4 h-4 mr-2" /> {t("Rediger")}</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setPrintTargets([p])} data-testid={`product-action-labels-${p.id}`}><QrCode className="w-4 h-4 mr-2" /> {t("Strekkode / etikett")}</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate(`/dashboard/butikk/ordrer?product=${p.id}`)} data-testid={`product-action-orders-${p.id}`}><ShoppingBag className="w-4 h-4 mr-2" /> {t("Ordrer med varen")}</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => toggleActive(p)} data-testid={`product-action-toggle-${p.id}`}>{p.active ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />} {p.active ? t("Skjul") : t("Vis")}</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => markSoldOut(p)} disabled={p.stock === 0} data-testid={`product-action-soldout-${p.id}`}><AlertTriangle className="w-4 h-4 mr-2" /> {t("Marker utsolgt")}</DropdownMenuItem>
                                    <DropdownMenuItem asChild><a href={productUrl(data.settings.organizer_id, p.id)} target="_blank" rel="noreferrer" data-testid={`product-action-view-${p.id}`}><ExternalLink className="w-4 h-4 mr-2" /> {t("Se vare")}</a></DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]" onClick={() => setDeleteTarget(p)} data-testid={`product-action-delete-${p.id}`}><Trash2 className="w-4 h-4 mr-2" /> {t("Slett")}</DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Card>
                  {/* Mobil: kortliste */}
                  <div className="space-y-3 md:hidden" data-testid="product-card-list">
                    {visible.map((p) => {
                      const st = productStatusMeta(p, t);
                      return (
                        <Card key={p.id} className="border-border" data-testid={`product-mobile-card-${p.id}`}>
                          <CardContent className="p-4 flex gap-3">
                            <Link to={`/dashboard/butikk/vare/${p.id}`} className="w-20 h-16 rounded-md overflow-hidden bg-muted shrink-0"><ProductImage product={p} /></Link>
                            <div className="min-w-0 flex-1 space-y-1.5">
                              <div className="flex items-start justify-between gap-2">
                                <Link to={`/dashboard/butikk/vare/${p.id}`} className="font-medium leading-snug line-clamp-2">{p.name}</Link>
                                <Badge className={`shrink-0 ${st.className}`} data-testid={`product-status-${p.id}-m`}>{st.label}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground tabular-nums">{p.sku && <span className="font-mono text-xs mr-2" data-testid={`product-sku-${p.id}-m`}>{p.sku}</span>}{nok(p.price)} · {t("Solgt")}: {p.sold_count || 0}</p>
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">{t("Lager")}</span><StockCell product={p} onSaved={replaceProduct} sfx="-m" /></div>
                                <div className="flex items-center gap-1">
                                  <Button asChild variant="ghost" size="icon" aria-label={t("Rediger")} data-testid={`product-action-edit-${p.id}-m`}><Link to={`/dashboard/butikk/vare/${p.id}`}><Pencil className="w-4 h-4" /></Link></Button>
                                  <Button variant="ghost" size="icon" onClick={() => setPrintTargets([p])} aria-label={t("Strekkode / etikett")} data-testid={`product-action-labels-${p.id}-m`}><QrCode className="w-4 h-4" /></Button>
                                  <Button variant="ghost" size="icon" onClick={() => toggleActive(p)} aria-label={p.active ? t("Skjul") : t("Vis")} data-testid={`product-action-toggle-${p.id}-m`}>{p.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </section>

            {/* Siste ordrer */}
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-heading font-semibold tracking-tight">{t("Siste ordrer")}</h2>
                <Button asChild variant="ghost" size="sm" data-testid="shop-all-orders-link"><Link to="/dashboard/butikk/ordrer">{t("Se ordrer")}</Link></Button>
              </div>
              {data.recent_orders.length === 0 ? (
                <Card className="border-dashed border-border" data-testid="empty-orders-state"><CardContent className="p-8 text-center text-sm text-muted-foreground">{t("Når noen kjøper i Butikk, dukker ordrene opp her.")}</CardContent></Card>
              ) : (
                <Card className="border-border">
                  <ul className="divide-y divide-border" data-testid="shop-recent-orders">
                    {data.recent_orders.map((o) => {
                      const st = orderStatusMeta(o);
                      return (
                        <li key={o.id} className="p-4 flex items-center justify-between gap-3">
                          <Link to={`/dashboard/butikk/ordrer?q=${encodeURIComponent(o.order_number)}`} className="min-w-0 group" data-testid={`recent-order-${o.id}`}>
                            <p className="font-medium truncate group-hover:underline underline-offset-2"><span className="font-mono text-xs text-muted-foreground mr-2">{o.order_number}</span>{o.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")} · {formatDateTime(o.created_at)}</p>
                          </Link>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="font-semibold tabular-nums text-sm">{nok(o.total_amount)}</span>
                            <Badge className={st.className}>{st.label}</Badge>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              )}
            </section>
          </>
        )}
      </div>

      <LabelPrintDialog open={!!printTargets} onOpenChange={(open) => !open && setPrintTargets(null)} products={printTargets || []} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Slette varen?")}</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.name} – {t("Varen fjernes fra butikken. Ordrer som allerede er lagt inn beholdes.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:opacity-95" data-testid="product-delete-confirm">{t("Ja, slett")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
